import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import { API } from "@/api/api";
import { useAppStore } from "@/store/app.store";
import { TrailerType, TruckAdditionalEquipment, TruckDoorType, TruckEquipment, TruckTypeList } from "@/constants/truck";
import type { TruckDto, TruckRoutesResponse, TruckRouteWithTruckNumber } from "@/api/trucks/dto/truck.dto";

export function useTruck() {
  const { t } = useI18n();
  const appStore = useAppStore();
  const api = new API();

  const isTruckRoutesLoading = ref<boolean>(false);
  const truckRoutes = ref<TruckRouteWithTruckNumber[]>([]);

  const truckStatusAvailable = "a";
  const truckStatusWillBeAvailable = "w";
  const truckStatusOnHold = "h";
  const truckStatusInRoute = "r";
  const truckStatusNotAvailable = "n";

  const truckStatuses = computed(() => [
    {
      id: truckStatusNotAvailable,
      name: t("trucks.status-n"),
      forTrip: true,
    },
    {
      id: truckStatusOnHold,
      name: t("trucks.status-h"),
    },
    {
      id: truckStatusAvailable,
      name: t("trucks.status-a"),
      disabled: true,
      forTrip: true,
    },
    {
      id: truckStatusWillBeAvailable,
      name: t("trucks.status-w"),
      disabled: true,
    },
    {
      id: truckStatusInRoute,
      name: t("trucks.status-r"),
      disabled: true,
    },
  ]);

  const trucksWithTrailer = computed(() => TruckTypeList.filter((t) => t.hasTrailer).map((t) => t.id));

  function getEquipments(arr: any[], value: string[]): string[] {
    const map = Object.fromEntries(arr.map((item) => [item.id, item.name]));
    return value.map((key) => map[key] || "");
  }

  function getTruckSize(truck: any): string {
    return `${truck.trailer.length}″ x ${truck.trailer.width}″ x ${truck.trailer.height}″`;
  }

  function getTruckType(type: string): string {
    return TruckTypeList.find((t) => t.id === type)?.name || "";
  }

  function getReservationTime(truck: any): number {
    if (!truck.reservedTill) return -1;
    return dayjs(truck.reservedTill).diff(dayjs(), "minutes");
  }

  function isReservedByDispatcher(truck: any, dispatcherId: number): boolean {
    return truck.reservedById === dispatcherId;
  }

  function getReservedBy(truck: any): string {
    if (truck.reservedById === appStore.dispatcher?.id) return "(you)";
    return appStore.getDispatcher(truck.reservedById)?.name || "";
  }

  function equipmentToString(truck: TruckDto) {
    if (!truck.trailer?.equipment || truck.trailer?.equipment.length === 0) {
      return "-";
    }
    return truck.trailer.equipment.map((e) => getEquipmentName(e)).join(", ");
  }

  function additionalEquipmentToString(truck: TruckDto) {
    if (!truck.trailer?.additionalEquipment || truck.trailer?.additionalEquipment.length === 0) {
      return "-";
    }
    return truck.trailer.additionalEquipment.map((e) => getAdditionalEquipmentName(e)).join(", ");
  }

  function getEquipmentName(id: string) {
    return TruckEquipment.find((e) => e.id === id)?.name || "-";
  }

  function getAdditionalEquipmentName(id: string) {
    return TruckAdditionalEquipment.find((e) => e.id === id)?.name || "-";
  }

  function doorTypeToString(truck: TruckDto) {
    if (!truck.trailer?.doorType) {
      return "-";
    }
    return TruckDoorType.find((dt) => dt.id === truck.trailer?.doorType)?.name;
  }

  function withTruckNumber(truckRoutesInfo: TruckRoutesResponse): TruckRouteWithTruckNumber[] {
    return truckRoutesInfo.routes.map((route) => {
      return {
        ...route,
        truckNumber: truckRoutesInfo.truckNumber,
      };
    });
  }

  async function loadTrucksRoutes(truckIds: number[]): Promise<void> {
    isTruckRoutesLoading.value = true;

    try {
      const responses = await Promise.all(truckIds.map((truckId) => api.trucks.getTruckRoutes(truckId)));

      truckRoutes.value = responses.flatMap((response) => {
        return response.success && response.data ? withTruckNumber(response.data) : [];
      });
    } catch (error) {
      console.error(error);
    } finally {
      isTruckRoutesLoading.value = false;
    }
  }

  return {
    isTruckRoutesLoading,
    truckRoutes,
    loadTrucksRoutes,

    truckStatusAvailable,
    truckStatusWillBeAvailable,
    truckStatusOnHold,
    truckStatusInRoute,
    truckStatusNotAvailable,
    truckStatuses,

    doorTypes: TruckDoorType,
    equipment: TruckEquipment,
    additionalEquipment: TruckAdditionalEquipment,
    trailerTypes: TrailerType,
    trucksWithTrailer,

    getEquipments,
    getTruckSize,
    getTruckType,
    getReservationTime,
    isReservedByDispatcher,
    getReservedBy,
    equipmentToString,
    additionalEquipmentToString,
    doorTypeToString,
  };
}
